import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

function About() {
  const initialStats = [
    { label: "Products", value: 0, target: 1200 },
    { label: "Customers", value: 0, target: 850 },
    { label: "Dealers", value: 0, target: 120 },
    { label: "Experience", value: 0, target: 15 },
  ];

  const [stats, setStats] = useState(initialStats);
  const [team, setTeam] = useState([]);

  // ✅ Fetch team data
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await fetch("/data.json");
        const data = await res.json();
        setTeam(data.team || []);
      } catch (error) {
        console.error("Error fetching team:", error);
      }
    };

    fetchTeam();
  }, []);

  // ✅ Animated counter (safe cleanup)
  useEffect(() => {
    const interval = setInterval(() => {
      setStats((prev) => {
        let completed = true;

        const updated = prev.map((item) => {
          if (item.value < item.target) {
            completed = false;

            const increment = Math.ceil(item.target / 80);
            return {
              ...item,
              value: Math.min(item.value + increment, item.target),
            };
          }
          return item;
        });

        if (completed) clearInterval(interval);

        return updated;
      });
    }, 40);

    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      text: "Best quality bike cables I’ve used. Smooth and durable!",
      name: "Raj Motor Works",
    },
    {
      text: "Reliable supplier with consistent performance.",
      name: "Speed Garage",
    },
  ];

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <Header />

      {/* HERO SECTION */}
      <section className="relative h-[65vh] flex items-center justify-center">
        <img
          src="https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=1600"
          alt="Bike"
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative text-center px-6">
          <h1 className="text-5xl md:text-7xl font-bold">
            Precision. Power. Performance.
          </h1>
          <p className="mt-5 text-gray-300 max-w-2xl mx-auto">
            We design premium bike cable systems engineered for riders who
            demand control, safety, and durability.
          </p>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="py-24 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <img
          src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200"
          alt="Workshop"
          className="rounded-3xl w-full h-[520px] object-cover"
        />

        <div>
          <span className="text-lime-400 uppercase tracking-widest text-sm">
            Who We Are
          </span>

          <h2 className="text-4xl md:text-5xl font-bold mt-4">
            Trusted Bike Cable Specialists
          </h2>

          <p className="text-gray-400 mt-6 leading-relaxed">
            BikeCable is a performance-focused manufacturer of motorcycle cable
            systems designed for precision control and long-term reliability.
          </p>

          <p className="text-gray-400 mt-4 leading-relaxed">
            From standard brake cables to custom engineered solutions, we serve
            workshops, dealers, and riders across India with consistent quality.
          </p>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="bg-zinc-900 py-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 text-center gap-10">
          {stats.map((item, i) => (
            <div key={i} className="hover:scale-105 transition">
              <h3 className="text-4xl font-bold text-lime-400">
                {item.value}+
              </h3>
              <p className="text-gray-400 mt-2">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-24 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10">
        {/* Mission */}
        <div className="bg-gradient-to-br from-zinc-900 to-black p-10 rounded-3xl border border-zinc-800 hover:border-lime-400 transition">
          <h3 className="text-3xl font-bold text-lime-400">Our Mission</h3>

          <p className="mt-4 text-gray-300 leading-relaxed">
            Deliver safe, reliable, and high-performance cable systems that
            enhance every rider’s experience.
          </p>

          <p className="mt-4 text-gray-400 leading-relaxed">
            We are committed to continuous innovation, strict quality control,
            and precision engineering.
          </p>

          <p className="mt-4 text-gray-400 leading-relaxed">
            Our goal is to support riders, workshops, and dealers with
            dependable solutions that improve safety and control.
          </p>
        </div>

        {/* Vision */}
        <div className="bg-gradient-to-br from-zinc-900 to-black p-10 rounded-3xl border border-zinc-800 hover:border-lime-400 transition">
          <h3 className="text-3xl font-bold text-lime-400">Our Vision</h3>

          <p className="mt-4 text-gray-300 leading-relaxed">
            To become a globally trusted leader in motorcycle cable engineering
            through innovation and quality.
          </p>

          <p className="mt-4 text-gray-400 leading-relaxed">
            We aim to set industry benchmarks using technology and
            craftsmanship.
          </p>

          <p className="mt-4 text-gray-400 leading-relaxed">
            Our vision is to build a globally respected performance brand.
          </p>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 max-w-6xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-center mb-12">Our Journey</h2>

        <div className="space-y-6 border-l border-lime-400 pl-6">
          <p className="text-gray-400">
            <span className="text-lime-400 font-bold">2010:</span> Company
            founded with a small workshop.
          </p>
          <p className="text-gray-400">
            <span className="text-lime-400 font-bold">2015:</span> Expanded
            dealer network across India.
          </p>
          <p className="text-gray-400">
            <span className="text-lime-400 font-bold">2020:</span> Introduced
            high-performance cable systems.
          </p>
          <p className="text-gray-400">
            <span className="text-lime-400 font-bold">2026:</span> Serving 800+
            workshops nationwide.
          </p>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-zinc-900 py-20">
        <h2 className="text-4xl font-bold text-center mb-12">Our Team</h2>

        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-center">
          {team.length === 0 ? (
            <p className="text-gray-400 col-span-3">Loading team...</p>
          ) : (
            team.map((t, i) => (
              <div key={i} className="bg-black p-8 rounded-3xl">
                <h3 className="text-xl font-bold text-lime-400">{t.name}</h3>
                <p className="text-gray-400 mt-2">{t.role}</p>
              </div>
            ))
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 max-w-6xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-center mb-12">
          What Clients Say
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-zinc-900 p-8 rounded-3xl">
              <p className="text-gray-300 italic">"{t.text}"</p>
              <p className="text-lime-400 mt-4 font-bold">- {t.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center">
        <h2 className="text-5xl font-bold">Ready to Upgrade Your Ride?</h2>
        <p className="text-gray-400 mt-4">
          Explore premium bike cable solutions built for performance.
        </p>

        <button className="mt-8 bg-lime-400 text-black px-8 py-4 rounded-full font-bold hover:bg-lime-300 transition">
          Explore Products
        </button>
      </section>

      <Footer />
    </div>
  );
}

export default About;
